import React, { useState } from 'react';
import { styled } from 'styletron-react';
import { useTenantBranding } from '../../hooks/useTenantBranding';
import { useS3Image } from '../../hooks/useS3Image';
import { usePublicPageScroll } from '../../hooks/usePublicPageScroll';

const Page = styled('div', {
  minHeight: '100vh',
  backgroundColor: '#fff',
  fontFamily:
    "'Red Hat Text', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  color: '#111827',
});

const Content = styled('div', {
  maxWidth: '760px',
  margin: '0 auto',
  padding: '24px 20px 80px',
});

const Header = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  marginBottom: '24px',
});

const HeaderLeft = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const Logo = styled('img', {
  height: '40px',
  objectFit: 'contain',
});

const TenantName = styled('div', {
  fontSize: '16px',
  fontWeight: '600',
});

const LangToggle = styled('button', (props: { $active: boolean }) => ({
  border: 'none',
  background: props.$active ? '#111827' : '#F3F4F6',
  color: props.$active ? '#fff' : '#111827',
  padding: '6px 12px',
  borderRadius: '999px',
  fontSize: '13px',
  fontWeight: '600',
  cursor: 'pointer',
}));

const Title = styled('h1', {
  fontSize: '32px',
  fontWeight: '700',
  margin: '8px 0 4px',
  lineHeight: '1.2',
});

const Subtitle = styled('p', {
  color: '#6B7280',
  fontSize: '14px',
  margin: '0 0 28px',
});

const SectionTitle = styled('h2', {
  fontSize: '20px',
  fontWeight: '700',
  margin: '28px 0 12px',
});

const Paragraph = styled('p', {
  fontSize: '15px',
  lineHeight: '1.6',
  margin: '0 0 12px',
});

const List = styled('ul', {
  paddingLeft: '20px',
  margin: '0 0 16px',
});

const Item = styled('li', {
  fontSize: '15px',
  lineHeight: '1.7',
  marginBottom: '6px',
});

const ContactBlock = styled('div', {
  background: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  padding: '16px 20px',
  marginTop: '24px',
});

type Lang = 'en' | 'es';

const TEXT = {
  en: {
    title: 'Terms of Service',
    effective: 'Effective date',
    intro:
      'These Terms of Service ("Terms") govern your access to and use of our mobile applications, websites, and related services (the "Services"). By creating an account or using the Services, you agree to be bound by these Terms.',
    sections: {
      eligibility: 'Eligibility',
      account: 'Your Account',
      acceptable: 'Acceptable Use',
      content: 'User Content',
      access: 'Access & Invitations',
      payments: 'Payments & Refunds',
      ip: 'Intellectual Property',
      thirdParty: 'Third-Party Services',
      disclaimer: 'Disclaimer of Warranties',
      liability: 'Limitation of Liability',
      termination: 'Termination',
      changes: 'Changes to These Terms',
      law: 'Governing Law',
      contact: 'Contact',
    },
    eligibility:
      'You must be at least 13 years old (and the age of majority in your jurisdiction) to use the Services. You confirm that you have the legal capacity to enter into these Terms. Property administrators must have authorization from the property owner to manage residents and guests on their behalf.',
    account: [
      'You are responsible for safeguarding your credentials. Activity in your account is your responsibility.',
      'You must provide accurate, current and complete information when registering, including identity documents required for access verification.',
      'You may not share your account with others or impersonate another person.',
      'We may suspend or terminate accounts that violate these Terms or pose a security risk.',
    ],
    acceptable: [
      'Do not use the Services for unlawful, fraudulent, or abusive purposes.',
      'Do not attempt to disable, reverse-engineer, or interfere with the Services.',
      'Do not upload content that infringes intellectual property, contains malware, or violates privacy of others.',
      'Do not use access features (gate codes, QR invitations) to grant entry to persons not authorized by the property administration.',
    ],
    content:
      'You retain rights to content you upload (photos, profile image, document scans). You grant us a limited license to host and display that content as necessary to operate the Services. You represent that you have the right to upload that content.',
    access:
      'Invitation, access code, and booking features are tools to facilitate property operations. Final authorization for physical access is determined by the property administrator and on-site personnel. We do not guarantee admittance based on a digital invitation alone.',
    payments: [
      'Bookings and other paid features are processed by a PCI-compliant payment processor (CyberSource). We do not store full payment card details.',
      'Refunds, if applicable, follow the cancellation policy displayed at the time of booking. Property administrators set those policies.',
      'Currency, taxes and fees are shown before you confirm a payment.',
    ],
    ip:
      'The Services, including all software, designs, trademarks, and content (excluding your user content), are owned by us or our licensors and protected by intellectual property laws. You may not copy or distribute the Services without our written consent.',
    thirdParty:
      'The Services rely on third-party providers (e.g., AWS, Cognito, CyberSource, Firebase). Your use of those services may be subject to their own terms. We are not responsible for outages or behavior of third-party providers.',
    disclaimer:
      'The Services are provided "as is" and "as available" without warranties of any kind. We do not guarantee uninterrupted availability, error-free operation, or fitness for a particular purpose.',
    liability:
      'To the maximum extent permitted by law, we are not liable for indirect, incidental, special, or consequential damages, or for any loss of data, profits, or revenue arising from your use of the Services. Our total liability for any claim is limited to the amount you paid us in the 12 months preceding the claim.',
    termination:
      'You may stop using the Services at any time and delete your account in-app (Profile → Delete User). We may suspend or terminate your access if you violate these Terms or for security/legal reasons. Some provisions survive termination (intellectual property, liability, governing law).',
    changes:
      'We may update these Terms from time to time. Material changes will be notified in-app or via email. Continued use after the changes constitutes acceptance.',
    law:
      'These Terms are governed by the laws of the Dominican Republic, without regard to conflict-of-laws principles. Disputes will be resolved in the competent courts of the Dominican Republic, unless mandatory consumer-protection laws of your jurisdiction provide otherwise.',
    contactBody:
      'For questions or notices related to these Terms, contact us at:',
  },
  es: {
    title: 'Términos de Servicio',
    effective: 'Fecha de vigencia',
    intro:
      'Estos Términos de Servicio ("Términos") rigen tu acceso y uso de nuestras aplicaciones móviles, sitios web y servicios relacionados (los "Servicios"). Al crear una cuenta o usar los Servicios, aceptas estar sujeto a estos Términos.',
    sections: {
      eligibility: 'Elegibilidad',
      account: 'Tu Cuenta',
      acceptable: 'Uso Aceptable',
      content: 'Contenido del Usuario',
      access: 'Acceso e Invitaciones',
      payments: 'Pagos y Reembolsos',
      ip: 'Propiedad Intelectual',
      thirdParty: 'Servicios de Terceros',
      disclaimer: 'Renuncia de Garantías',
      liability: 'Limitación de Responsabilidad',
      termination: 'Terminación',
      changes: 'Cambios a estos Términos',
      law: 'Ley Aplicable',
      contact: 'Contacto',
    },
    eligibility:
      'Debes tener al menos 13 años (y la mayoría de edad en tu jurisdicción) para usar los Servicios. Confirmas que tienes capacidad legal para aceptar estos Términos. Los administradores de propiedad deben contar con autorización del propietario para gestionar residentes e invitados en su nombre.',
    account: [
      'Eres responsable de proteger tus credenciales. La actividad en tu cuenta es tu responsabilidad.',
      'Debes proporcionar información precisa, actual y completa al registrarte, incluyendo documentos de identidad requeridos para verificación de acceso.',
      'No puedes compartir tu cuenta ni hacerte pasar por otra persona.',
      'Podemos suspender o cancelar cuentas que violen estos Términos o representen un riesgo de seguridad.',
    ],
    acceptable: [
      'No uses los Servicios para fines ilegales, fraudulentos o abusivos.',
      'No intentes desactivar, hacer ingeniería inversa, o interferir con los Servicios.',
      'No subas contenido que infrinja propiedad intelectual, contenga malware, o viole la privacidad de terceros.',
      'No uses funciones de acceso (códigos de portería, QR de invitación) para permitir el ingreso de personas no autorizadas por la administración.',
    ],
    content:
      'Conservas los derechos sobre el contenido que subes (fotos, foto de perfil, escaneos de documentos). Nos otorgas una licencia limitada para alojar y mostrar ese contenido en la medida necesaria para operar los Servicios. Declaras tener derecho a subir ese contenido.',
    access:
      'Las funciones de invitación, código de acceso y reservaciones son herramientas para facilitar las operaciones de la propiedad. La autorización final del acceso físico la determina el administrador de la propiedad y el personal en sitio. No garantizamos el ingreso solo por contar con una invitación digital.',
    payments: [
      'Las reservaciones y otras funciones de pago se procesan a través de un procesador PCI-compliant (CyberSource). No almacenamos números completos de tarjeta.',
      'Los reembolsos, si aplican, siguen la política de cancelación mostrada al momento de reservar. Estas políticas las definen los administradores de la propiedad.',
      'Moneda, impuestos y cargos se muestran antes de confirmar el pago.',
    ],
    ip:
      'Los Servicios, incluyendo software, diseños, marcas y contenido (excluyendo tu contenido de usuario), son propiedad nuestra o de nuestros licenciantes y están protegidos por leyes de propiedad intelectual. No puedes copiar o distribuir los Servicios sin nuestro consentimiento escrito.',
    thirdParty:
      'Los Servicios dependen de proveedores externos (AWS, Cognito, CyberSource, Firebase, etc.). Su uso puede estar sujeto a sus propios términos. No somos responsables de fallas o comportamiento de proveedores externos.',
    disclaimer:
      'Los Servicios se proveen "tal cual" y "según disponibilidad", sin garantías de ningún tipo. No garantizamos disponibilidad ininterrumpida, operación libre de errores, ni idoneidad para un propósito particular.',
    liability:
      'En la máxima medida permitida por ley, no somos responsables por daños indirectos, incidentales, especiales o consecuentes, ni por pérdida de datos, ganancias o ingresos derivados del uso de los Servicios. Nuestra responsabilidad total por cualquier reclamación se limita al monto que nos pagaste en los 12 meses previos al reclamo.',
    termination:
      'Puedes dejar de usar los Servicios en cualquier momento y eliminar tu cuenta dentro de la app (Perfil → Eliminar Usuario). Podemos suspender o cancelar tu acceso si violas estos Términos o por razones de seguridad/legales. Algunas cláusulas sobreviven a la terminación (propiedad intelectual, responsabilidad, ley aplicable).',
    changes:
      'Podemos actualizar estos Términos periódicamente. Cambios materiales se notificarán en la app o por correo. El uso continuado tras los cambios constituye aceptación.',
    law:
      'Estos Términos se rigen por las leyes de la República Dominicana, sin considerar principios de conflicto de leyes. Las disputas se resolverán en los tribunales competentes de la República Dominicana, salvo que leyes imperativas de protección al consumidor de tu jurisdicción dispongan lo contrario.',
    contactBody:
      'Para preguntas o notificaciones relacionadas con estos Términos, contáctanos en:',
  },
} as const;

const EFFECTIVE_DATE = '2026-05-14';

export const TermsOfService: React.FC = () => {
  usePublicPageScroll();
  const initial: Lang =
    (typeof navigator !== 'undefined' &&
      navigator.language?.toLowerCase().startsWith('es'))
      ? 'es'
      : 'en';
  const [lang, setLang] = useState<Lang>(initial);
  const t = TEXT[lang];

  const { tenant } = useTenantBranding();
  const tenantName = tenant?.name || 'City Place';
  const { imageUrl: logoUrl } = useS3Image(tenant?.settings?.logo);
  const supportEmail =
    (tenant?.settings as any)?.supportEmail || 'info@xhinola.io';

  return (
    <Page>
      <Content>
        <Header>
          <HeaderLeft>
            {logoUrl ? <Logo src={logoUrl} alt={tenantName} /> : null}
            <TenantName>{tenantName}</TenantName>
          </HeaderLeft>
          <div style={{ display: 'flex', gap: '6px' }}>
            <LangToggle $active={lang === 'en'} onClick={() => setLang('en')}>
              EN
            </LangToggle>
            <LangToggle $active={lang === 'es'} onClick={() => setLang('es')}>
              ES
            </LangToggle>
          </div>
        </Header>

        <Title>{t.title}</Title>
        <Subtitle>
          {t.effective}: {EFFECTIVE_DATE}
        </Subtitle>

        <Paragraph>{t.intro}</Paragraph>

        <SectionTitle>{t.sections.eligibility}</SectionTitle>
        <Paragraph>{t.eligibility}</Paragraph>

        <SectionTitle>{t.sections.account}</SectionTitle>
        <List>
          {t.account.map((item, i) => (
            <Item key={i}>{item}</Item>
          ))}
        </List>

        <SectionTitle>{t.sections.acceptable}</SectionTitle>
        <List>
          {t.acceptable.map((item, i) => (
            <Item key={i}>{item}</Item>
          ))}
        </List>

        <SectionTitle>{t.sections.content}</SectionTitle>
        <Paragraph>{t.content}</Paragraph>

        <SectionTitle>{t.sections.access}</SectionTitle>
        <Paragraph>{t.access}</Paragraph>

        <SectionTitle>{t.sections.payments}</SectionTitle>
        <List>
          {t.payments.map((item, i) => (
            <Item key={i}>{item}</Item>
          ))}
        </List>

        <SectionTitle>{t.sections.ip}</SectionTitle>
        <Paragraph>{t.ip}</Paragraph>

        <SectionTitle>{t.sections.thirdParty}</SectionTitle>
        <Paragraph>{t.thirdParty}</Paragraph>

        <SectionTitle>{t.sections.disclaimer}</SectionTitle>
        <Paragraph>{t.disclaimer}</Paragraph>

        <SectionTitle>{t.sections.liability}</SectionTitle>
        <Paragraph>{t.liability}</Paragraph>

        <SectionTitle>{t.sections.termination}</SectionTitle>
        <Paragraph>{t.termination}</Paragraph>

        <SectionTitle>{t.sections.changes}</SectionTitle>
        <Paragraph>{t.changes}</Paragraph>

        <SectionTitle>{t.sections.law}</SectionTitle>
        <Paragraph>{t.law}</Paragraph>

        <SectionTitle>{t.sections.contact}</SectionTitle>
        <Paragraph>{t.contactBody}</Paragraph>
        <ContactBlock>
          <div style={{ fontWeight: 600 }}>{tenantName}</div>
          <div>
            <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
          </div>
        </ContactBlock>
      </Content>
    </Page>
  );
};
